import { useFacetFactory } from '@vue-storefront/core';
import type { Context, FacetSearchResult } from '@vue-storefront/core';
import type { Category, FacetResultsData } from '~/types/commercetools';
import { addEmptyChildren, removeEmptyCategories } from '~/helpers/category/categoriesTree';
import { CUSTOM_QUERIES } from '~/constants/customQueries';
import { extractAncestorsFromCategory } from '~/helpers/category/extractAncestorsFromCategory';
import { addCategoryToCategoryAncestors } from '~/helpers/category/addCategoryToCategoryAncestors';
import { buildTreeFromArray } from '~/helpers/category/buildTreeFromArray';
import { sortCategories } from '~/helpers/category/sortCategories';
import { runExpondoMiddlewareRequest } from '~/helpers/middleware/runExpondoMiddlewareRequest';
import { AllowedMethods } from '~/types/expondoMiddleware';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~/constants/routes';

type FacetResultsDataExtended = FacetResultsData & {
  activeCategories: Category[],
}

/*
 * VSF native method
 * fetches categories for category page
 * copied into source code to add modifications to categories fetch (use custom queries) and to process the result
 */
const useFacetFactoryParams = {
  search: async (
    _context: Context,
    params?: FacetSearchResult<FacetResultsDataExtended>,
  ): Promise<FacetResultsDataExtended> => {
    const acceptLanguage = params?.input.languageAndCountry;
    const { data: categoryResponse } = await runExpondoMiddlewareRequest({
      $axios: params?.input?.$axios,
      $config: params?.input?.$config,
      method: AllowedMethods.post,
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.ROOT + EXPONDO_MIDDLEWARE_ROUTES.CT.GET_CATEGORY,
      data: [
        {
          slug: params?.input?.categorySlug,
          acceptLanguage,
          fetchSiblings: params?.input?.fetchSiblings,
        },
        {
          categories: CUSTOM_QUERIES.CATEGORIES.LISTING,
        },
      ],
      headers: params?.input?.headers,
    });
    const categoryRawResults: Category[] = categoryResponse?.data?.categories?.results || [];
    const currentCategoryResults: Category[] = removeEmptyCategories(categoryRawResults, acceptLanguage);

    const currentCategory = currentCategoryResults[0];
    sortCategories(currentCategory?.children);
    const ancestors = extractAncestorsFromCategory(currentCategory);
    addCategoryToCategoryAncestors(ancestors, currentCategory);

    let categories: Category[] = [];
    categories = addEmptyChildren(buildTreeFromArray(ancestors));

    if (categories[0]) {
      // head of tree
      categories[0].parent = null;
    }

    return {
      // Pass empty products, because now products are fetched from algolia
      results: [],
      categories,
      facets: [],
      rootCategory: categories[0],
      currentCategory,
      activeCategories: ancestors,
      total: 0,
      attributeDefinitions: [],
    };
  },
};

const useFacet = useFacetFactory<FacetResultsDataExtended>(useFacetFactoryParams);

export {
  useFacet,
  useFacetFactoryParams,
};
